import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, ChevronRight } from "lucide-react";
import { SERVICES, COMPANY } from "@/data/content";
import SEOHead from "@/components/SEOHead";
import AICalculator from "@/components/AICalculator";
import ContactForm from "@/components/ContactForm";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/leistungen" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "provider": { "@type": "GeneralContractor", "name": COMPANY.name },
    "areaServed": "NRW",
    "description": service.description,
  };

  return (
    <>
      <SEOHead
        title={`${service.title} – ${COMPANY.name} | Köln & NRW`}
        description={service.short}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-[#0C2749] text-white overflow-hidden">
        <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C2749] via-[#0C2749]/80 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <nav className="flex items-center gap-2 text-xs text-white/60 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white">Start</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/leistungen" className="hover:text-white">Leistungen</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{service.title}</span>
          </nav>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2C80C8] mb-4">Leistung</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "Outfit" }}>
            {service.title}
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">{service.short}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/preisrechner" className="btn-shimmer inline-flex items-center gap-2 bg-white text-[#0C2749] hover:bg-[#F5F5F5] px-7 py-4 rounded-sm font-medium" data-testid="service-cta-calc">
              Preis berechnen <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 px-7 py-4 rounded-sm font-medium" data-testid="service-cta-call">
              <Phone className="w-4 h-4" /> Direkt anrufen
            </a>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>Was wir bei {service.title} liefern</h2>
            <p className="text-slate-700 leading-relaxed mb-8">{service.description}</p>
            <ul className="space-y-3">
              {service.bullets.concat(["Eigene erfahrene Bauleitung vor Ort", "Wochenreports und transparente Kommunikation", "Festpreisgarantie nach Vor-Ort-Besichtigung"]).map((b) => (
                <li key={b} className="flex items-start gap-3 text-[#0C2749]">
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center bg-grad-primary text-white rounded-sm flex-shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-[#F5F5F5] p-8 rounded-sm border border-slate-200 sticky top-28">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2873B8] mb-3">Schnelle Anfrage</p>
              <p className="text-[#0C2749] font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Festpreisangebot zu {service.title}</p>
              <p className="text-sm text-slate-600 mb-6">Wir melden uns binnen 24 Stunden mit einer kostenlosen Erstberatung.</p>
              <ContactForm source={`leistung-${service.slug}`} compact />
            </div>
          </aside>
        </div>
      </section>

      {/* AI Calculator */}
      <section className="bg-[#F5F5F5] py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2873B8] mb-4">KI-Preisrechner</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0C2749]" style={{ fontFamily: "Outfit" }}>
              Sofort-Indikation für {service.title}
            </h2>
          </div>
          <AICalculator compact />
        </div>
      </section>

      {/* Other services */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0C2749] mb-10" style={{ fontFamily: "Outfit" }}>Weitere Leistungen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/leistungen/${o.slug}`} className="bg-[#F5F5F5] hover:bg-[#0C2749] p-6 rounded-sm transition-colors group" data-testid={`other-service-${o.slug}`}>
                <p className="text-sm font-semibold text-[#0C2749] group-hover:text-white" style={{ fontFamily: "Outfit" }}>{o.title}</p>
                <p className="text-xs text-slate-500 group-hover:text-white/70 mt-2">{o.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
